import * as Yup from "yup";
import { capitalize } from "common/utils/strings";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { Divider, MenuItem, TextField, Typography } from "@mui/material";
import { Formik } from "formik";
import { Job } from "protos/rtc/jobs";
import { ListRobotsResponse } from "protos/rtc/location_history";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";
import React, { FC } from "react";

interface AutoCompleteRowsModalProps {
  job: Job;
  availableTractors: ListRobotsResponse["robots"] | undefined;
  open: boolean;
  onSave?: () => void;
  onClose?: () => void;
}

interface AutoCompleteRowsFormValues {
  tractorSerial: string;
  lookbackHours: number;
}

export const AutoCompleteRowsModal: FC<AutoCompleteRowsModalProps> = ({
  job,
  availableTractors,
  open,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  const [autoCompleteRows] = useMutationPopups(
    rtcJobsApi.endpoints.autoCompleteRows.useMutation(),
    {
      success: capitalize(t("utils.actions.updated")),
    }
  );

  const initialValues: AutoCompleteRowsFormValues = {
    tractorSerial: availableTractors?.[0]?.serial ?? "",
    lookbackHours: 12,
  };

  const validationSchema = Yup.object({
    tractorSerial: Yup.string().required(t("utils.form.required")),
    lookbackHours: Yup.number()
      .required(t("utils.form.required"))
      .min(1, t("utils.form.mustBeGreaterThan", { min: 1 }))
      .max(168, t("utils.form.mustBeLessThan", { max: 168 })),
  });

  const handleSubmit = async (
    values: AutoCompleteRowsFormValues
  ): Promise<void> => {
    await autoCompleteRows({
      jobId: job.id,
      robotSerials: [values.tractorSerial],
      lookbackWindow: { seconds: values.lookbackHours * 3600, nanos: 0 },
    });
    onSave?.();
  };

  if (!open) {
    return;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        isSubmitting,
        handleReset,
        errors,
        touched,
        submitForm,
      }) => (
        <ConfirmationDialog
          title={t("views.autotractor.autoCompleteRows.title")}
          yesText={t("views.autotractor.autoCompleteRows.completeRows")}
          yesDisabled={isSubmitting}
          onYes={submitForm}
          onClose={() => {
            handleReset();
            onClose?.();
          }}
          description={
            <>
              <div className="flex gap-1">
                <InfoIcon fontSize="small" />
                <Typography className="flex my-auto">
                  {t("views.autotractor.autoCompleteRows.warning")}
                </Typography>
              </div>
              <Divider />
              <div className="flex gap-4 mt-2">
                <TextField
                  select
                  className="flex-1"
                  label={capitalize(t("models.equipment.tractor_one"))}
                  value={values.tractorSerial}
                  onChange={(e) =>
                    setFieldValue("tractorSerial", e.target.value)
                  }
                >
                  {availableTractors?.map((tractor) => (
                    <MenuItem key={tractor.serial} value={tractor.serial}>
                      {tractor.serial}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="number"
                  className="w-28"
                  label={capitalize(t("utils.durations.hour_few"))}
                  value={values.lookbackHours}
                  onChange={(e) =>
                    setFieldValue("lookbackHours", Number(e.target.value))
                  }
                  error={touched.lookbackHours && Boolean(errors.lookbackHours)}
                  helperText={
                    touched.lookbackHours && errors.lookbackHours
                      ? errors.lookbackHours
                      : ""
                  }
                  slotProps={{
                    htmlInput: { min: 1, max: 168, step: 1 },
                  }}
                />
              </div>
            </>
          }
        />
      )}
    </Formik>
  );
};
